import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import type { ElevationPoint, RouteData } from '../../types';
import { 
  getCategoryColor,
  type SurfaceCategory 
} from '../../utils/routeCalculation';

// Function to get icons for surface categories
const getSurfaceIcon = (category: SurfaceCategory): string => {
  const icons = {
    'paved': '🛣️',
    'gravel': '🪨',
    'unpaved': '🛤️', // Railway track icon better represents unpaved trails/paths
    'cobblestone': '🧱',
    'grass': '🌱',
    'rough': '⚠️',
    'unknown': '❓'
  };
  
  return icons[category] || '❓';
};

// Surface bar component
interface SurfaceBarProps {
  routeData: RouteData;
  elevationData: ElevationPoint[];
  surfaceIndicatorPosition: number;
  surfaceIsDragging: boolean;
  setSurfaceIndicatorPosition: (position: number) => void;
  setSurfaceIsDragging: (dragging: boolean) => void;
  onHover?: (index: number | null) => void;
}

const SurfaceBar: React.FC<SurfaceBarProps> = ({
  routeData,
  elevationData,
  surfaceIndicatorPosition,
  surfaceIsDragging,
  setSurfaceIndicatorPosition,
  setSurfaceIsDragging,
  onHover
}) => {
  // Move all hooks to the top before any conditional returns
  const surfaceBarRef = useRef<HTMLDivElement>(null);
  
  // Use passed props instead of parent state
  const indicatorPosition = surfaceIndicatorPosition;
  const isDragging = surfaceIsDragging;
  const setIndicatorPosition = setSurfaceIndicatorPosition;
  const setIsDragging = setSurfaceIsDragging;

  // SIMPLIFIED TOUCH HANDLING - optimized for real devices
  useEffect(() => {
    const surfaceBar = surfaceBarRef.current;
    if (!surfaceBar) return;

    let touchDragActive = false;
    let mouseDragActive = false;
    let surfaceTouchId: number | null = null;

    // Helper function for updating hover - local to avoid stale closures
    const updateHoverLocal = (percentage: number) => {
      if (!onHover || !routeData.coordinates || routeData.coordinates.length === 0) return;
      
      // Use coordinates array (with distance) instead of elevation data for better accuracy
      const coordinates = routeData.coordinates;
      const totalDistance = coordinates[coordinates.length - 1]?.distance || 0;
      const targetDistance = (percentage / 100) * totalDistance;
      
      // Find the closest coordinate point by distance
      let closestIndex = 0;
      let minDistance = Math.abs(coordinates[0].distance || 0 - targetDistance);
      
      for (let i = 1; i < coordinates.length; i++) {
        const distance = Math.abs((coordinates[i].distance || 0) - targetDistance);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = i;
        }
      }
      
      onHover?.(closestIndex);
    };    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      
      touchDragActive = true;
      surfaceTouchId = e.touches[0].identifier;
      e.preventDefault();
      
      const rect = surfaceBar.getBoundingClientRect();
      const x = e.touches[0].clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      
      setIndicatorPosition(percentage);
      setIsDragging(true);
      updateHoverLocal(percentage);
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (!touchDragActive || surfaceTouchId === null) return;
      
      let ourTouch = null;
      for (let i = 0; i < e.touches.length; i++) {
        if (e.touches[i].identifier === surfaceTouchId) {
          ourTouch = e.touches[i];
          break;
        }
      }
      
      if (!ourTouch) return;
      
      e.preventDefault();
      
      const rect = surfaceBar.getBoundingClientRect();
      const x = ourTouch.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      
      setIndicatorPosition(percentage);
      updateHoverLocal(percentage);
    };

    const handleGlobalTouchEnd = (e: TouchEvent) => {
      if (!touchDragActive || surfaceTouchId === null) return;
      
      let ourTouchEnded = true;
      for (let i = 0; i < e.touches.length; i++) {
        if (e.touches[i].identifier === surfaceTouchId) {
          ourTouchEnded = false;
          break;
        }
      }
      
      if (ourTouchEnded) {
        touchDragActive = false;
        surfaceTouchId = null;
        setIsDragging(false);
      }
    };

    // Mouse handlers for desktop
    const handleMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      mouseDragActive = true;
      
      const rect = surfaceBar.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      
      setIndicatorPosition(percentage);
      setIsDragging(true);
      updateHoverLocal(percentage);
    };

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!mouseDragActive) return;
      
      e.preventDefault();
      
      // Always get fresh reference to surface bar
      const currentSurfaceBar = surfaceBarRef.current;
      if (!currentSurfaceBar) return;
      
      const rect = currentSurfaceBar.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      
      setIndicatorPosition(percentage);
      updateHoverLocal(percentage);
    };

    // Handle mouse hover over surface bar (not dragging)
    const handleSurfaceBarMouseMove = (e: MouseEvent) => {
      if (mouseDragActive) return; // Skip if we're already dragging
      
      const rect = surfaceBar.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      
      setIndicatorPosition(percentage);
      updateHoverLocal(percentage);
    };

    // Handle mouse leave to clear hover
    const handleSurfaceBarMouseLeave = () => {
      if (mouseDragActive) return; // Don't clear during drag
      onHover?.(null);
    };

    const handleGlobalMouseUp = () => {
      if (mouseDragActive) {
        mouseDragActive = false;
        setIsDragging(false);
      }
    };

    // Add event listeners
    surfaceBar.addEventListener('touchstart', handleTouchStart, { passive: false });
    surfaceBar.addEventListener('mousedown', handleMouseDown);
    surfaceBar.addEventListener('mousemove', handleSurfaceBarMouseMove);
    surfaceBar.addEventListener('mouseleave', handleSurfaceBarMouseLeave);
    
    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    document.addEventListener('touchend', handleGlobalTouchEnd, { passive: true });
    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      surfaceBar.removeEventListener('touchstart', handleTouchStart);
      surfaceBar.removeEventListener('mousedown', handleMouseDown);
      surfaceBar.removeEventListener('mousemove', handleSurfaceBarMouseMove);
      surfaceBar.removeEventListener('mouseleave', handleSurfaceBarMouseLeave);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [setIndicatorPosition, setIsDragging, onHover, elevationData, routeData]);

  if (!routeData.segments || routeData.segments.length === 0) {
    return null;
  }

  // Calculate cumulative distances for positioning
  let cumulativeDistance = 0;
  const totalDistanceKm = (Number(routeData.distance) || 0) / 1000;
  
  // First, calculate the sum of all segment distances to compare with total route distance
  const segmentDistanceSum = routeData.segments.reduce((sum, seg) => {
    const segDist = (Number(seg.Distance) || 0) / 1000;
    return sum + segDist;
  }, 0);
  
  // ALWAYS use the total route distance as reference - this is what should match the cycling map
  const referenceDistance = totalDistanceKm;
  
  // Calculate scaling factor if segments don't match route distance
  const scalingFactor = segmentDistanceSum > 0 ? referenceDistance / segmentDistanceSum : 1;
  
  const segmentPositions = routeData.segments.map((segment, index) => {
    const segmentDistance = (Number(segment.Distance) || 0) / 1000;
    // Scale the segment distance to fit within the total route distance
    const scaledSegmentDistance = segmentDistance * scalingFactor;
    
    const startPosition = cumulativeDistance;
    cumulativeDistance += scaledSegmentDistance;
    const endPosition = cumulativeDistance;
    
    const startPercent = referenceDistance > 0 ? (startPosition / referenceDistance) * 100 : 0;
    const widthPercent = referenceDistance > 0 ? (scaledSegmentDistance / referenceDistance) * 100 : 0;
    
    return {
      index,
      segment,
      startPosition,
      endPosition,
      startPercent,
      widthPercent,
      surface: segment.surface || 'unknown',
      category: segment.surfaceCategory || 'unknown'
    };
  });

  // Get the current surface at the indicator position
  const getCurrentSurfaceAtPosition = (percentage: number) => {
    // Always use total route distance to match the cycling map
    const currentDistanceKm = (percentage / 100) * totalDistanceKm;
    
    // Find which segment contains this distance
    for (const position of segmentPositions) {
      // Use inclusive start and exclusive end, except for the last segment
      const isLastSegment = position.index === segmentPositions.length - 1;
      const isInSegment = currentDistanceKm >= position.startPosition && 
                         (isLastSegment ? currentDistanceKm <= position.endPosition : currentDistanceKm < position.endPosition);
      
      if (isInSegment) {
        return {
          surface: position.surface,
          category: position.category,
          segmentDistance: position.endPosition - position.startPosition,
          positionInSegment: currentDistanceKm - position.startPosition,
          segmentIndex: position.index
        };
      }
    }
    
    // Fallback to last segment if we're at the very end
    if (segmentPositions.length > 0) {
      const lastSegment = segmentPositions[segmentPositions.length - 1];
      return {
        surface: lastSegment.surface,
        category: lastSegment.category,
        segmentDistance: lastSegment.endPosition - lastSegment.startPosition,
        positionInSegment: lastSegment.endPosition - lastSegment.startPosition,
        segmentIndex: lastSegment.index
      };
    }
    
    return null;
  };

  const currentSurface = getCurrentSurfaceAtPosition(indicatorPosition);
  
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography 
        variant="caption" 
        sx={{ 
          fontSize: '0.7rem', 
          color: 'text.secondary',
          mb: 0.5,
          display: 'block'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            Surface: 
            {currentSurface ? (
              <span style={{ 
                color: getCategoryColor(currentSurface.category as SurfaceCategory),
                fontWeight: 600,
                marginLeft: '4px'
              }}>
                {getSurfaceIcon(currentSurface.category as SurfaceCategory)} {currentSurface.surface.replace(/_/g, ' ')}
              </span>
            ) : (
              <span> Unknown Surface</span>
            )}
          </span>
          
          {/* Distance indicator - only show when dragging or hovering */}
          {(isDragging || indicatorPosition > 0) && (
            <span style={{
              fontSize: '0.7rem',
              fontWeight: 600,
              color: '#2196F3',
              fontFamily: 'monospace',
              backgroundColor: isDragging ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
              padding: isDragging ? '2px 6px' : '0',
              borderRadius: '4px',
              transition: 'all 0.2s ease'
            }}>
              {((indicatorPosition / 100) * totalDistanceKm).toFixed(2)} km
            </span>
          )}
        </Box>
      </Typography>
      
      {/* SURFACE BAR with visual surface segments */}
      <Box
        ref={surfaceBarRef}
        sx={{
          height: '24px',
          width: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          position: 'relative',
          cursor: isDragging ? 'grabbing' : 'pointer',
          // Allow all touch interactions for drag functionality
          touchAction: 'none', // Disable default touch actions to allow custom drag handling
          userSelect: 'none',
          overflow: 'hidden',
          // Create the visual surface gradient with proper segment boundaries
          background: segmentPositions.length > 0 ? 
            `linear-gradient(to right, ${segmentPositions.map((pos, index) => {
              const color = getCategoryColor(pos.category as SurfaceCategory);
              const startPercent = pos.startPercent;
              const endPercent = pos.startPercent + pos.widthPercent;
              
              // Ensure segments connect properly without gaps
              if (index === 0) {
                // First segment: start at 0%
                return `${color} 0%, ${color} ${endPercent}%`;
              } else if (index === segmentPositions.length - 1) {
                // Last segment: end at 100%
                return `${color} ${startPercent}%, ${color} 100%`;
              } else {
                // Middle segments: use calculated percentages
                return `${color} ${startPercent}%, ${color} ${endPercent}%`;
              }
            }).join(', ')})` : 
            '#f0f0f0', // Fallback color if no segments
        }}
      >
        {/* Single draggable indicator - no conflicting elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '-8px',
            bottom: '-8px',
            width: '8px',
            backgroundColor: isDragging ? '#ff4444' : '#2196F3',
            cursor: isDragging ? 'grabbing' : 'grab',
            left: `calc(${indicatorPosition}% - 4px)`,
            zIndex: 10,
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
            border: '2px solid white',
            pointerEvents: 'none', // Let touches pass through to parent
            '&:hover': {
              backgroundColor: '#ff4444',
              transform: 'scaleX(1.2)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.7)',
            },
            transition: isDragging ? 'none' : 'all 0.2s ease',
          }}
          title="Drag to move along route"
        />
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        mt: 0.5,
        fontSize: '0.65rem',
        color: 'text.secondary'
      }}>
        <span>0 km</span>
        <span>{totalDistanceKm.toFixed(1)} km</span>
      </Box>
    </Box>
  );
};

interface SurfaceProfileProps {
  routeData: RouteData;
  elevationData: ElevationPoint[];
  onHover: (index: number | null) => void;
  onSurfaceHighlight?: (surface: string | null, segmentIndex?: number) => void;
  onClearSurfaceHighlight?: () => void;
  highlightedSurface?: string | null;
}

export const SurfaceProfile: React.FC<SurfaceProfileProps> = ({
  routeData,
  elevationData,
  onHover,
  onSurfaceHighlight,
  highlightedSurface
}) => {
  // State for surface highlighting
  const [selectedSurface, setSelectedSurface] = useState<string | null>(null);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState<number>(0);

  // State for surface bar indicator - moved to parent to persist across re-renders
  const [surfaceIndicatorPosition, setSurfaceIndicatorPosition] = useState(10);
  const [surfaceIsDragging, setSurfaceIsDragging] = useState(false);

  // Reset surface indicator position when route changes
  useEffect(() => {
    // Reset to beginning when route data changes (new route loaded or extended)
    setSurfaceIndicatorPosition(0);
  }, [routeData.distance, routeData.segments?.length]); // Reset when distance or segment count changes

  // Sync local selection state with parent highlighting state
  useEffect(() => {
    // If parent clears the highlighting, clear local selection as well
    if (highlightedSurface === null) {
      setSelectedSurface(null);
      setCurrentSegmentIndex(0);
    }
  }, [highlightedSurface]);

  // Handle surface selection with segment cycling
  const handleSurfaceClick = (surface: string) => {
    if (selectedSurface === surface) {
      // Same surface clicked - cycle to next segment
      const surfaceSegments = routeData.segments?.filter(seg => seg.surface === surface) || [];
      
      if (surfaceSegments.length > 0) {
        const nextIndex = (currentSegmentIndex + 1) % surfaceSegments.length;
        setCurrentSegmentIndex(nextIndex);
        
        // Call the callback with the new segment index
        onSurfaceHighlight?.(surface, nextIndex);
      }
    } else {
      // New surface selected - reset to first segment
      setSelectedSurface(surface);
      setCurrentSegmentIndex(0);
      
      onSurfaceHighlight?.(surface, 0);
    }
  };

  // Handle clicking on surface item to highlight ALL segments of that type and fit complete route
  const handleSurfaceHighlightAll = (surface: string) => {
    // Toggle behavior: if this surface is already selected and we're showing all segments (-1), then clear
    if (selectedSurface === surface && highlightedSurface === surface && highlightedSurface !== null) {
      // Clear highlighting
      setSelectedSurface(null);
      setCurrentSegmentIndex(0);
      onSurfaceHighlight?.(null, 0);
    } else {
      // Set the surface as selected (for visual feedback)
      setSelectedSurface(surface);
      setCurrentSegmentIndex(0); // Reset to first segment for visual indicator
      
      // Call the parent to highlight all segments of this surface type
      // We use -1 as a special flag to indicate "highlight all segments"
      onSurfaceHighlight?.(surface, -1);
    }
  };

  // Function to analyze surface data from segments by surfaces (not categories)
  const analyzeSurfaceData = (segments: any[]) => {
    if (!segments || segments.length === 0) {
      return [];
    }

    // Group by surface type
    const surfaceMap = new Map<string, { 
      surface: string;
      category: SurfaceCategory;
      totalDistance: number; 
      segmentCount: number;
      originalSurfaces: Map<string, { distance: number; count: number }>;
    }>();
    
    segments.forEach(segment => {
      if (segment.surface && segment.Distance !== undefined && segment.Distance !== null && segment.surfaceCategory) {
        const surface = segment.surface;
        const distance = Number(segment.Distance) / 1000; // Convert from meters to kilometers
        
        // Skip if distance is not a valid number
        if (isNaN(distance) || distance <= 0) {
          return;
        }
        
        if (!surfaceMap.has(surface)) {
          surfaceMap.set(surface, {
            surface,
            category: segment.surfaceCategory,
            totalDistance: 0,
            segmentCount: 0,
            originalSurfaces: new Map()
          });
        }
        
        const surfaceData = surfaceMap.get(surface)!;
        surfaceData.totalDistance += distance;
        surfaceData.segmentCount++;
        
        // Track original surfaces if they exist
        const originalSurface = segment.originalSurface || surface;
        if (!surfaceData.originalSurfaces.has(originalSurface)) {
          surfaceData.originalSurfaces.set(originalSurface, { distance: 0, count: 0 });
        }
        const originalData = surfaceData.originalSurfaces.get(originalSurface)!;
        originalData.distance += distance;
        originalData.count++;
      }
    });

    // Convert to array and sort by distance descending
    return Array.from(surfaceMap.values())
      .sort((a, b) => b.totalDistance - a.totalDistance);
  };

  // Function to render surface overview with icons and colors
  const renderSurfaceOverview = () => {
    if (!routeData.segments) {
      return (
        <Typography variant="body2" color="text.secondary" style={{ fontStyle: 'italic', textAlign: 'center', marginTop: '20px' }}>
          No Surface data available
        </Typography>
      );
    }

    const surfaceData = analyzeSurfaceData(routeData.segments);
    const totalDistance = surfaceData.reduce((sum, item) => sum + item.totalDistance, 0);

    if (surfaceData.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" style={{ fontStyle: 'italic', textAlign: 'center', marginTop: '20px' }}>
          No Surface data available
        </Typography>
      );
    }

    return (
      <Box sx={{ mt: 0.5 }}>
        {surfaceData.map((surfaceInfo) => {
          const percentage = totalDistance > 0 ? (surfaceInfo.totalDistance / totalDistance) * 100 : 0;
          const isSelected = selectedSurface === surfaceInfo.surface;
          const hasSelection = selectedSurface !== null;
          const isOtherSelected = hasSelection && !isSelected;
          
          // Get original surfaces for this category, sorted by distance
          const originalSurfaces = Array.from(surfaceInfo.originalSurfaces.entries())
            .map(([surface, data]) => ({ surface, ...data }))
            .sort((a, b) => b.distance - a.distance);
          
          // Only show original surfaces if there are multiple or if they differ from the main surface
          const showOriginalSurfaces = isSelected && originalSurfaces.length > 0 && 
            (originalSurfaces.length > 1 || originalSurfaces[0].surface !== surfaceInfo.surface);
          
          return (
            <React.Fragment key={surfaceInfo.surface}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 0.5,
                  px: 0.75,
                  mb: 0.25,
                  backgroundColor: isSelected 
                    ? getCategoryColor(surfaceInfo.category) + '30' 
                    : getCategoryColor(surfaceInfo.category) + '15',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: isSelected 
                    ? `2px solid ${getCategoryColor(surfaceInfo.category)}` 
                    : `1px solid ${getCategoryColor(surfaceInfo.category)}40`,
                  opacity: isOtherSelected ? 0.4 : 1, // Make non-selected items transparent when something is selected
                  '&:hover': {
                    backgroundColor: isSelected 
                      ? getCategoryColor(surfaceInfo.category) + '40'
                      : getCategoryColor(surfaceInfo.category) + '25',
                    opacity: 1 // Full opacity on hover
                  }
                }}
                onClick={() => handleSurfaceHighlightAll(surfaceInfo.surface)}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  flex: 1 
                }}>
                  {/* Icon */}
                  <Box sx={{ fontSize: '1.2rem', minWidth: '24px' }}>
                    {getSurfaceIcon(surfaceInfo.category)}
                  </Box>
                  
                  {/* Surface name */}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.8rem',
                      textTransform: 'capitalize',
                      flex: 1,
                      fontWeight: isSelected ? 600 : 500,
                      color: '#333' // Keep consistent dark color for better readability
                    }}
                  >
                    {surfaceInfo.surface.replace(/_/g, ' ')}
                  </Typography>
                </Box>
                
                {/* Distance, percentage, and magnifying glass */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1.5,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem'
                }}>
                  {/* Magnifying glass for cycling through segments */}
                  <Box
                    sx={{
                      minWidth: '35px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1rem',
                      '&:hover': {
                        backgroundColor: getCategoryColor(surfaceInfo.category) + '40',
                        borderRadius: '4px'
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering the parent onClick
                      handleSurfaceClick(surfaceInfo.surface);
                    }}
                    title={`Cycle through ${surfaceInfo.segmentCount} segments`}
                  >
                    🔍
                    {isSelected && (
                      <Typography variant="caption" sx={{ 
                        fontSize: '0.6rem', 
                        ml: 0.5,
                        color: 'text.secondary'
                      }}>
                        {currentSegmentIndex + 1}/{surfaceInfo.segmentCount}
                      </Typography>
                    )}
                  </Box>
                  
                  <Typography variant="body2" sx={{ 
                    minWidth: '55px', 
                    textAlign: 'right', 
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    color: '#333' // Keep consistent dark color
                  }}>
                    {surfaceInfo.totalDistance.toFixed(2)} km
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      minWidth: '40px', 
                      textAlign: 'right',
                      fontSize: '0.7rem',
                      color: 'text.secondary' // Keep consistent color
                    }}
                  >
                    {percentage.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
              
              {/* Original surfaces breakdown when selected */}
              {showOriginalSurfaces && (
                <Box sx={{ ml: 3, mb: 1 }}>
                  <Typography variant="caption" sx={{ 
                    fontSize: '0.65rem', 
                    color: 'text.secondary',
                    fontWeight: 500,
                    mb: 0.5,
                    display: 'block'
                  }}>
                    Original surfaces:
                  </Typography>
                  {originalSurfaces.map((originalSurface) => {
                    const origPercentage = surfaceInfo.totalDistance > 0 ? (originalSurface.distance / surfaceInfo.totalDistance) * 100 : 0;
                    return (
                      <Box 
                        key={originalSurface.surface}
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          py: 0.25,
                          px: 0.5,
                          mb: 0.25,
                          backgroundColor: getCategoryColor(surfaceInfo.category) + '20',
                          borderRadius: '4px',
                          borderLeft: `3px solid ${getCategoryColor(surfaceInfo.category)}`,
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.7rem',
                            textTransform: 'capitalize',
                            flex: 1,
                            color: '#555',
                            fontStyle: originalSurface.surface === surfaceInfo.surface ? 'normal' : 'italic'
                          }}
                        >
                          {originalSurface.surface.replace(/_/g, ' ')}
                          {originalSurface.surface === surfaceInfo.surface && ' (aggregated)'}
                        </Typography>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: 1,
                          fontFamily: 'monospace',
                          fontSize: '0.65rem'
                        }}>
                          <Typography variant="body2" sx={{ 
                            minWidth: '45px', 
                            textAlign: 'right', 
                            fontSize: '0.65rem',
                            color: '#555'
                          }}>
                            {originalSurface.distance.toFixed(2)} km
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              minWidth: '35px', 
                              textAlign: 'right',
                              fontSize: '0.6rem',
                              color: 'text.secondary'
                            }}
                          >
                            {origPercentage.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  return (
    <Box 
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box 
        sx={{ 
          flex: 1, 
          overflowY: 'auto',
          minHeight: 0,
          // Allow normal touch interactions, don't restrict them
          touchAction: 'auto',
          px: 0.25, // 2px left and right padding
          pr: 1, // 8px right padding for space between content and scrollbar
          // Consistent scrollbar styling
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '3px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '3px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.3)' } },
        }}
      >
        <SurfaceBar
          routeData={routeData}
          elevationData={elevationData}
          surfaceIndicatorPosition={surfaceIndicatorPosition}
          surfaceIsDragging={surfaceIsDragging}
          setSurfaceIndicatorPosition={setSurfaceIndicatorPosition}
          setSurfaceIsDragging={setSurfaceIsDragging}
          onHover={onHover}
        />
        {renderSurfaceOverview()}
      </Box>
    </Box>
  );
};
